import { useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import Popover from "@mui/material/Popover";
import { formatDeclineReasonLabel } from "../../utils/verificationLabels";

export interface DeclineReasonsListProps {
  reasons: string[];
  /** How many reasons to show inline before the rest go behind "show more...". */
  maxVisible?: number;
}

// Shows the first `maxVisible` decline reasons inline; any remainder opens in a
// popover anchored to "show more..." rather than expanding the surrounding
// layout (grid row height, detail page card), matching DonutChartLegend's
// overflow pattern.
function DeclineReasonsList({ reasons, maxVisible = 2 }: DeclineReasonsListProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const { t } = useTranslation();

  if (reasons.length === 0) {
    return <>—</>;
  }

  const visible = reasons.slice(0, maxVisible);
  const overflow = reasons.slice(maxVisible);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    // Stop this from bubbling up to a parent row-click handler (e.g. the
    // verifications grid navigates to the detail page on row click) — this
    // link should only open the popover, not also trigger navigation.
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{ minWidth: 0 }}
      onClick={(event) => event.stopPropagation()}
    >
      <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
        {visible.map((reason) => t(formatDeclineReasonLabel(reason))).join(", ")}
      </Typography>
      {overflow.length > 0 && (
        <>
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="always"
            onClick={handleOpen}
            sx={{
              all: "unset",
              flexShrink: 0,
              fontSize: "inherit",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            {t("show more...")}
          </Link>
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <Stack spacing={0.5} sx={{ p: 1.5, minWidth: 220, maxWidth: 320 }}>
              {overflow.map((reason) => (
                <Typography key={reason} variant="body2">
                  {t(formatDeclineReasonLabel(reason))}
                </Typography>
              ))}
            </Stack>
          </Popover>
        </>
      )}
    </Stack>
  );
}

export default DeclineReasonsList;
